import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {createLinkedBranchMutation as createLinkedBranchQueryId} from '@github-ui/server-persisted-queries'
import type {
  CreateLinkedBranchInput as ServerCreateLinkedBranchInput,
  CreateLinkedBranchMutationMutation,
} from '@github-ui/server-persisted-queries/types/createLinkedBranchMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {commitMutation, type Environment, graphql, useRelayEnvironment} from 'react-relay'
import {createOperationDescriptor, getRequest} from 'relay-runtime'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import CreateLinkedBranchMutationGraphql, {
  type CreateLinkedBranchInput as RelayCreateLinkedBranchInput,
  type createLinkedBranchMutation,
  type createLinkedBranchMutation$data,
} from './__generated__/createLinkedBranchMutation.graphql'

export type CreateLinkedBranchInput = Omit<ServerCreateLinkedBranchInput, 'clientMutationId'>

type CreateLinkedBranchResponse = {
  readonly data?: CreateLinkedBranchMutationMutation | null
  readonly errors?: readonly unknown[] | null
}

type CreateLinkedBranchRoute = {
  owner: string
  repo: string
  number: number
}

const INVALID_RESPONSE_ERROR = 'Create linked branch response was invalid'

export async function fetchCreateLinkedBranch(
  {owner, repo, number}: CreateLinkedBranchRoute,
  input: CreateLinkedBranchInput,
): Promise<CreateLinkedBranchMutationMutation> {
  const {issueId: _routeBoundIssueId, ...routeSafeInput} = input
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${number}/linked_branches.json?queryId=${encodeURIComponent(
      createLinkedBranchQueryId,
    )}`,
    {
      method: 'POST',
      body: {input: routeSafeInput},
    },
  )

  if (!response.ok) throw new Error(`Create linked branch failed (${response.status})`)

  let json: CreateLinkedBranchResponse
  try {
    json = (await response.json()) as CreateLinkedBranchResponse
  } catch {
    throw new Error(INVALID_RESPONSE_ERROR)
  }

  const topLevelMessages = errorMessages(json.errors)
  if (topLevelMessages.length > 0) throw new Error(topLevelMessages.join(', '))
  if (!json.data) throw new Error(INVALID_RESPONSE_ERROR)

  const payload = json.data.createLinkedBranch
  if (!payload) throw new Error('Create linked branch payload was missing')

  const payloadMessages = errorMessages(payload.errors)
  if (payloadMessages.length > 0) {
    const duplicateMessage = ERRORS.branchAlreadyExists(input.name ?? '')
    if (payloadMessages.includes(duplicateMessage)) {
      const error = new Error(duplicateMessage)
      error.name = 'LinkedBranchExists'
      throw error
    }
    throw new Error(payloadMessages.join(', '))
  }

  if (!payload.linkedBranch) {
    const error = new Error('Linked branch not found')
    error.name = 'LinkedBranchNotFound'
    throw error
  }
  if (!payload.issue) throw new Error('Create linked branch issue was missing')

  return json.data
}

function errorMessages(errors: readonly unknown[] | null | undefined): string[] {
  if (!errors) return []

  return errors.flatMap(error => {
    if (!error || typeof error !== 'object' || !('message' in error)) return []
    return typeof error.message === 'string' && error.message.length > 0 ? [error.message] : []
  })
}

export function useCreateLinkedBranchMutation(route: CreateLinkedBranchRoute) {
  const queryClient = useQueryClient()
  const environment = useRelayEnvironment()

  return useMutation({
    mutationKey: ['issue-viewer', 'create-linked-branch', route],
    mutationFn: (input: CreateLinkedBranchInput) => fetchCreateLinkedBranch(route, input),
    onSuccess: async (data, input) => {
      await settleCreateLinkedBranchMutation({queryClient, environment, route, input, data})
    },
  })
}

export function useCreateLinkedBranchRelayMutation() {
  const environment = useRelayEnvironment()

  return useCallback(
    (options: Omit<Parameters<typeof commitCreateLinkedBranchMutation>[0], 'environment'>) =>
      commitCreateLinkedBranchMutation({...options, environment}),
    [environment],
  )
}

type QueryClient = ReturnType<typeof useQueryClient>

export async function settleCreateLinkedBranchMutation({
  queryClient,
  environment,
  route,
  input,
  data,
}: {
  queryClient: QueryClient
  environment: Environment
  route: CreateLinkedBranchRoute
  input: CreateLinkedBranchInput
  data: CreateLinkedBranchMutationMutation
}) {
  const issue = data.createLinkedBranch?.issue
  if (!issue) throw new Error('Create linked branch issue was missing')

  const operation: SetFieldsOperation = {
    type: 'setFields',
    id: issue.id,
    fields: {linkedBranches: issue.linkedBranches},
  }

  await settleConfirmedOperation({
    queryClient,
    targets: [{queryKey: issueViewerSecondaryViewQueryKey(route.owner, route.repo, route.number)}],
    ownerId: issue.id,
    operation,
  })

  commitCreateLinkedBranchPayload(environment, input, data)
}

// RELAY-CLEANUP(createLinkedBranchMutation): remove this Relay cache bridge (and the
// commitCreateLinkedBranchMutation FF-off hook below) once DevelopmentSection no longer
// consumes Relay-backed data.
function commitCreateLinkedBranchPayload(
  environment: Environment,
  input: CreateLinkedBranchInput,
  data: CreateLinkedBranchMutationMutation,
) {
  const operation = createOperationDescriptor(getRequest(CreateLinkedBranchMutationGraphql), {
    input: toRelayInput(input),
  })
  environment.commitPayload(operation, data)
}

// Adapts the server-generated input to the legacy Relay artifact during the feature-flag rollout.
function toRelayInput(input: CreateLinkedBranchInput): RelayCreateLinkedBranchInput {
  return {
    ...input,
    issueId: String(input.issueId),
    repositoryId: input.repositoryId == null ? input.repositoryId : String(input.repositoryId),
  }
}

export function commitCreateLinkedBranchMutation({
  environment,
  input,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: CreateLinkedBranchInput
  onError?: (error: Error) => void
  onCompleted?: (response: createLinkedBranchMutation$data) => void
}) {
  return commitMutation<createLinkedBranchMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation createLinkedBranchMutation($input: CreateLinkedBranchInput!) @raw_response_type {
        createLinkedBranch(input: $input) {
          linkedBranch {
            ref {
              id
              name
              prefix
            }
          }
          issue {
            ... on Issue {
              linkedBranches(first: 25) {
                nodes {
                  id
                  # Ref fields duplicated here (and in the refs list below)
                  # because we're removing Relay: no shared fragment, and a
                  # graphql tagged query can't interpolate a shared selection.
                  ref {
                    name
                    id
                    __typename
                    target {
                      oid
                      id
                      __typename
                    }
                    repository {
                      id
                      nameWithOwner
                      defaultBranchRef {
                        name
                        id
                        target {
                          oid
                          id
                          __typename
                        }
                        associatedPullRequests {
                          totalCount
                        }
                      }
                    }
                    associatedPullRequests {
                      totalCount
                    }
                  }
                }
              }
              repository {
                refs(refPrefix: "refs/heads/", first: 25) {
                  nodes {
                    name
                    id
                    __typename
                    target {
                      oid
                      id
                      __typename
                    }
                    repository {
                      id
                      nameWithOwner
                      defaultBranchRef {
                        name
                        id
                        target {
                          oid
                          id
                          __typename
                        }
                        associatedPullRequests {
                          totalCount
                        }
                      }
                    }
                    associatedPullRequests {
                      totalCount
                    }
                  }
                }
              }
            }
          }
          clientMutationId
          errors {
            message
          }
        }
      }
    `,
    variables: {input: toRelayInput(input)},
    onError: error => onError?.(error),
    onCompleted: response => {
      if (!response.createLinkedBranch?.linkedBranch) {
        if (response.createLinkedBranch?.errors) {
          response.createLinkedBranch.errors.map(e => {
            const message = ERRORS.branchAlreadyExists(input?.name ?? '')
            if (e.message === message) {
              onError?.({name: 'LinkedBranchExists', message})
            }
          })
        } else {
          onError?.({name: 'LinkedBranchNotFound', message: 'Linked branch not found'})
        }
      } else {
        onCompleted?.(response)
      }
    },
  })
}
